/* eslint-disable complexity */
import { useContext, useMemo, useState, useCallback, useEffect, memo } from 'react';
import { InteractionStepTaskComponent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/InteractionStep';
import {
  DEFAULT_PROCESS_SUPERVISION_SUBTITLE,
  DEFAULT_PROCESS_SUPERVISION_TITLE,
  DEFAULT_PROCESS_SUPERVISION_END_AFTER_RATINGS_TITLE,
  InteractionStepType,
  IProcessSupervisionChunk,
  ProcessSupervisionStepResponse,
  ContextType,
  DEFAULT_PROCESS_SUPERVISION_EDIT_CHUNKS_TITLE,
  DEFAULT_PROCESS_SUPERVISION_CREATE_CHUNKS_TITLE,
} from '@scale/shared/types/chat';
import { twMerge } from 'tailwind-merge';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { Icon, Typography, Button, SelectMenu, Skeleton, Tooltip } from '@scale/scaleui';
import { Alert, LaTeXEditor } from '../../TempComponents';
import _ from 'lodash';
import { useAsync } from 'react-use-v13';
import {
  useAllResponsesForStepType,
  useChatStoreExtract,
  useMostRecentMessageMetadata,
  useMostRecentPrompt,
  useStepParams,
  useStepResponse,
} from '../../../Chat/hooks/ChatStore.hooks';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';

import { StepWidthWrapper, StepWidth } from '../../step/StepWidthWrapper';
import {
  getIsComplete,
  getShouldResetHistory,
  getStepOutput,
  getShouldSubmit,
  replaceTemplateVariables,
} from './helpers';
import { ProcessChunkingStrategy, ProcessChunkingStrategyMap } from './chunking';
import {
  DEFAULT_PROCESS_CHUNKING_STRATEGY,
  DEFAULT_RATING_BUTTONS,
  DEFAULT_FIELDS_TO_COLLECT,
  ProcessSupervisionChunk,
} from './types';

import ContentTypeSelector, { Mode } from '../ModelResponseEditor/components/ContentTypeSelector';
import { ProcessStepsEditor } from './ProcessStepsEditor';
import { StepCard } from '../../step/StepCard';
import { PinButton } from '../../step/PinButton';
import { ChatMessageDisplayConfigButton } from '../../step/ChatMessageDisplayConfig';
import useProcessCritique, {
  ProcessCritiqueOutput,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useProcessCritique';
import ProcessCritique from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/ProcessCritique/ProcessCritiqueInput';
import { ChatMarkdown } from '../../components/ChatMarkdown';
import {
  filterUnecessaryCritiqueFields,
  processCritiqueFields,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/ProcessCritique/helpers';
import { CommentButton } from '../../feedback/CommentButton';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { getPreratedChunks } from './prerate';
import {
  Editor,
  EditorContainer,
  MarkdownFloatingMenu,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor';

import MarkdownEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/MarkdownEditorContext';
import PlainTextEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/PlainTextEditorContext';

import MarkdownToolbar from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/toolbar/MarkdownToolbar';
import { useMarkdownSyntaxOptions } from '../../hooks/useMarkdownSyntaxOptions';

// NOTE: This type is temporary, because I wanted to avoid fromatting the enire file
type ProcessSupervisionInteractionStepTaskComponentTypeTemporary = InteractionStepTaskComponent;

const ProcessSupervisionTaskComponent: ProcessSupervisionInteractionStepTaskComponentTypeTemporary =
  ({ readOnly, complete, responseIndex, isPinnedView }) => {
    const step = useStepParams<InteractionStepType.ProcessSupervision>(responseIndex);

    const taskContext = useContext(ChatTaskContext);

    // Params and their defaults
    const injectTaskerLocation =
      step.params?.process_chunking_strategy_params?.useWorkerLocation ?? false;

    const processChunkingStrategyName =
      step?.params?.process_chunking_strategy || DEFAULT_PROCESS_CHUNKING_STRATEGY;
    let processChunkingStrategyParams = step?.params?.process_chunking_strategy_params || {};

    if (injectTaskerLocation) {
      const workerId = taskContext.workerId;
      processChunkingStrategyParams = {
        ...processChunkingStrategyParams,
        workerId,
        useWorkerLocation: true,
      };
    }

    const endAfterRatingIds = useMemo(
      () => step?.params?.end_after_rating_ids || [-1],
      [step?.params?.end_after_rating_ids],
    );
    const reRequestModel = step?.params?.re_request_model ?? true;
    const rewriteFieldId = 'rewrite';
    const ratingButtons = step.params?.rating_buttons || DEFAULT_RATING_BUTTONS;
    const ratingValues = ratingButtons.map((rating: any) => rating.value);
    const ratingValueToLabel = (value: number) => {
      const button = ratingButtons.find((rating: any) => rating.value === value);
      return button?.label;
    };
    const minimumNegativeRatings = step.params?.min_negative_ratings || -1;

    const minReflectionsBeforeRewrite = step.params?.min_reflections_before_rewrite || 0;

    // Text configs
    const enableLatex = taskContext.milkdownEditorConfig?.enableMathPlugin ?? false;
    const {
      chatMessageDisplayConfig,
      useMarkdownDisplay,
      updateMarkdownConfig,
      toggleChatMessageDisplayType,
    } = useConfigureChatMessageDisplay();

    // Misc.
    const useMilkdown = true;

    const config = useChatStoreExtract(state => state.config);
    const response = useStepResponse<ProcessSupervisionStepResponse>(responseIndex);

    const markStepAsIncompletedByIndex = useChatStoreExtract(
      state => state.markStepAsIncompletedByIndex,
    );
    const markStepAsCompletedByIndex = useChatStoreExtract(
      state => state.markStepAsCompletedByIndex,
    );
    const canUpdateCompletedState = !_.isNil(responseIndex);

    const previousResponses = useAllResponsesForStepType(
      InteractionStepType.ProcessSupervision,
      responseIndex,
    );

    const mostRecentPrompt = useMostRecentPrompt(responseIndex);
    const mostRecentMessageMetadata = useMostRecentMessageMetadata(responseIndex) || {};

    // filter where response.context.prompt equals mostRecentPrompt?.output
    const previousResponsesForCurrentPrompt = previousResponses.filter(
      response =>
        response.context.prompt === mostRecentPrompt || response.context.skipPromptInput === true,
    );

    // check how many previous responses for the current prompt have a chunk with a reflection
    const numPreviousResponsesWithReflection = previousResponsesForCurrentPrompt.filter(response =>
      response.context.chunks.some(chunk => chunk.reflection),
    ).length;

    const shouldUseProcessCritique =
      step.params.enable_process_critique && !previousResponsesForCurrentPrompt?.length;
    const processCritiqueReviewLevels = step.params.process_critique_review_levels ?? [];
    const hasGtfa = step.params.has_gtfa ?? true;

    const processCritiqueHook = useProcessCritique(
      response?.context?.critiqueData as ProcessCritiqueOutput,
    );

    const { onSubmit } = useStepSubmitHandler({
      responseIndex,
    });

    // find the unique prompts for previous process supervision steps to count how many processes there are
    // include the current prompt in the count
    const numProcessSupervisions = [
      ...new Set([...previousResponses.map(response => response.context.prompt), mostRecentPrompt]),
    ].length;

    const previousPrompts = useAllResponsesForStepType(
      InteractionStepType.PromptInput,
      responseIndex,
    );

    const [isQuestionSolvedByRewrite, setIsQuestionSolvedByRewrite] = useState<boolean | undefined>(
      response?.context?.isQuestionSolvedByRewrite ?? undefined,
    );

    const chatResponse = useChatStoreExtract(state => {
      const responses = state.responses;

      // PREFILLING LOGIC
      // If it is the first response, AND they provided an initial_input, we want to use that
      const isFirstResponse = !(previousResponsesForCurrentPrompt?.length > 0);

      // however, if the initial input is dependent on the initial_prompt (that is, the CB has not edited yet),
      // then we want to ensure that the first prompt in the task matches the initial_prompt value
      // If not, we will ignore the prefilled response
      // TODO @dannyadkins update this to use the most recent prompt, and test (now that in theory we can have multiple prompts)
      const firstPromptInTask = previousPrompts?.[0];
      const initialPromptValue = replaceTemplateVariables(
        step.params.initial_prompt,
        config?.templateVariables,
      );
      const isPrefilledResponseValid =
        !step.params.initial_prompt || initialPromptValue === firstPromptInTask?.output;

      if (step.params.start_blank) {
        return {
          output: undefined,
        };
      }

      if (step.params.initial_input?.length && isFirstResponse && isPrefilledResponseValid) {
        const initialInput = replaceTemplateVariables(
          step.params.initial_input,
          config?.templateVariables,
        );
        return {
          output: initialInput,
        };
      } else if (step.params.input_step_id) {
        // find the LAST response for the input step type

        const foundStep = responses
          .slice()
          .reverse()
          .find(response => response.step_id === step.params.input_step_id);

        if (!foundStep) {
          throw new Error(
            `Could not find a response for step_id ${step.params.input_step_id} for ProcessSupervision task`,
          );
        }

        return foundStep;
      } else {
        // default: return for the most recent step
        return responses.at(responseIndex - 1);
      }
    });

    const [processSteps, setProcessSteps] = useState<ProcessSupervisionChunk[]>(
      response?.context?.chunks || [],
    );

    const [isEditingProcessSteps, setIsEditingProcessSteps] = useState(false);

    // STATE
    const { loading: processStepsIsLoading, error: processStepsError } = useAsync(async () => {
      if (processSteps?.length) {
        return processSteps;
      }

      // Process steps
      const processChunkingStrategy =
        ProcessChunkingStrategyMap[processChunkingStrategyName as ProcessChunkingStrategy];

      if (!chatResponse?.output) {
        return [];
      }

      const processChunkingStrategyPromise = processChunkingStrategy(
        chatResponse.output as string,
        processChunkingStrategyParams,
      );

      const { chunks } = await processChunkingStrategyPromise;

      const res = chunks
        .map(chunk => chunk.trim())
        .filter(chunk => chunk?.length)
        .map(chunk => ({ content: chunk }));

      try {
        const preratedChunks = await getPreratedChunks(res, step.params.prerating_strategies);
        setProcessSteps(preratedChunks);
        return preratedChunks;
      } catch (e) {
        setProcessSteps(res);
        setError('Failed to prerate chunks.');
        return res;
      }
    });

    const setFieldOnChunk = useCallback(
      (field: string, index: number, value: any) => {
        setProcessSteps(prevProcessSteps =>
          prevProcessSteps.map((step, i) => (i === index ? { ...step, [field]: value } : step)),
        );
      },
      [setProcessSteps],
    );

    const fieldsToCollect = useMemo(() => {
      const fields = [...(step.params?.fields_to_collect || DEFAULT_FIELDS_TO_COLLECT)];

      // add critique field if needed
      if (
        shouldUseProcessCritique &&
        fields.findIndex(field => field.fieldId === 'critique') === -1 &&
        !processCritiqueHook.critiqueData.error
      ) {
        fields.push(processCritiqueFields);
      }
      return fields;
    }, [
      shouldUseProcessCritique,
      step.params?.fields_to_collect,
      processCritiqueHook.critiqueData.error,
    ]);
    const rewriteRatingIds = useMemo(() => {
      if (!ratingButtons) {
        return [];
      }

      return ratingButtons
        .filter(button => {
          const fieldsToCollectForRating = fieldsToCollect?.filter(field =>
            field?.collectForRatingValues?.includes(button.value),
          );

          if (!fieldsToCollectForRating) {
            return false;
          }

          return fieldsToCollectForRating.some(field => field.fieldId === rewriteFieldId);
        })
        .map(button => button.value);
    }, [ratingButtons, fieldsToCollect, rewriteFieldId]);

    // will eventually deprecate
    const fieldEntries: Record<keyof ProcessSupervisionChunk, any[]> = useMemo(() => {
      const out: Record<keyof ProcessSupervisionChunk, any[]> = {};
      processSteps.forEach(step => {
        if (!out.rating) {
          out.rating = [];
        }
        out.rating.push(step.rating ?? undefined);

        fieldsToCollect.forEach((field: any) => {
          const fieldId = field.fieldId;

          const value = step[fieldId];
          if (!out[fieldId]) {
            out[fieldId] = [];
          }
          out[fieldId].push(value ?? undefined);
        });
      });
      return out;
    }, [fieldsToCollect, processSteps]);

    // These are temporary memos and setters to preserve backwards compatibility before adding custom fields
    // This will be deprecated soon
    const ratings = useMemo(() => fieldEntries.rating || [], [fieldEntries.rating]);
    const rewrites = useMemo(
      () => fieldEntries[rewriteFieldId] || [],
      [fieldEntries, rewriteFieldId],
    );

    const setRatings = useCallback(
      (index: number, value: number) => {
        setFieldOnChunk('rating', index, value);
      },
      [setFieldOnChunk],
    );

    // find the first bad rating index, where rating < 0
    const firstBadRatingIndex = useMemo(() => {
      if (!endAfterRatingIds?.length) {
        return 100000;
      }

      const num = Object.entries(ratings).find(([_index, rating]) =>
        endAfterRatingIds?.includes(rating),
      )?.[0];

      return num ? parseInt(num) : 100000;
    }, [ratings, endAfterRatingIds]);

    const [error, setError] = useState<string | undefined>('');

    const getFieldsToCollectAtIndex = useCallback(
      (index: number) => {
        const fieldsToCollectAtIndex = [];
        for (const field of fieldsToCollect) {
          if (firstBadRatingIndex < index) {
            continue;
          }
          // TODO later: check if field is required/should be conditionally shown in params
          if (field.collectForRatingValues.includes(ratings[index])) {
            fieldsToCollectAtIndex.push(field);
          }
        }

        filterUnecessaryCritiqueFields({
          fields: fieldsToCollectAtIndex,
          rating: ratings[index],
          currentStep: index + 1,
          critiqueStep: processCritiqueHook.critiqueData.step ?? -1,
        });
        return fieldsToCollectAtIndex;
      },
      [fieldsToCollect, firstBadRatingIndex, ratings, processCritiqueHook.critiqueData.step],
    );

    const isEmpty = (type: string, value: any) => {
      if (type === 'markdown' || type === 'plaintext') {
        return !value || value === '';
      }
      return _.isUndefined(value);
    };

    const getFieldsAreCompleteAtIndex = useCallback(
      (index: number) => {
        const fieldsToCollectAtIndex = getFieldsToCollectAtIndex(index);
        if (fieldsToCollectAtIndex.length === 0) {
          return { fieldsAreComplete: true };
        }

        // loop through each field at that processSteps index and check if its defined
        for (const field of fieldsToCollectAtIndex) {
          const fieldId = field.fieldId;
          if (isEmpty(field.type, fieldEntries?.[fieldId]?.[index]) && field.required) {
            return { fieldsAreComplete: false, fieldId };
          }
        }

        if (
          reRequestModel &&
          _.isUndefined(isQuestionSolvedByRewrite) &&
          rewriteRatingIds?.includes(Number(ratings?.[index]))
        ) {
          return { fieldsAreComplete: false, fieldId: 'isQuestionSolvedByRewrite' };
        }

        return { fieldsAreComplete: true };
      },
      [
        fieldEntries,
        getFieldsToCollectAtIndex,
        isQuestionSolvedByRewrite,
        reRequestModel,
        rewriteRatingIds,
        ratings,
      ],
    );

    const convertProcessStepsToChunks = useCallback(
      (processSteps: any) => {
        return processSteps.map((chunk: ProcessSupervisionChunk, index: number) => {
          // only keep the fields that are relevant to the chunk
          const fieldsToCollectAtIndex = getFieldsToCollectAtIndex(index);

          const newChunk: any = {
            rating: chunk.rating,
            content: chunk.content,
          };
          for (const fieldId of fieldsToCollectAtIndex?.map(field => field.fieldId) ?? []) {
            newChunk[fieldId] = fieldEntries?.[fieldId]?.[index];
          }
          return newChunk;
        });
      },
      [fieldEntries, getFieldsToCollectAtIndex],
    );

    const getErrorMessage = useCallback(() => {
      if (processSteps?.length && ratingButtons.length > 0) {
        // check if all of the ratings up to the first bad rating are defined
        for (let i = 0; i < Math.min(firstBadRatingIndex, processSteps.length); i++) {
          const ratingIsDefined = !_.isUndefined(ratings[i]);
          if (!ratingIsDefined) {
            return `Please rate all steps`;
          }
        }

        // for each index, check if the fields are complete
        // if not, then set the error message
        for (let i = 0; i < processSteps.length; i++) {
          const { fieldsAreComplete, fieldId } = getFieldsAreCompleteAtIndex(i);
          if (!fieldsAreComplete) {
            return `Please fill out all required fields for step ${i} in this turn: ${fieldId}.`;
          }
        }
      }
      return '';
    }, [processSteps, ratings, firstBadRatingIndex, getFieldsAreCompleteAtIndex, ratingButtons]);

    useEffect(() => {
      const errorMessage = getErrorMessage();
      setError(errorMessage);
    }, [getErrorMessage]);

    useEffect(() => {
      const newChunks = convertProcessStepsToChunks(processSteps);
      const savedChunks = response?.context.chunks;
      const matchesLastSavedResponse = _.isEqual(newChunks, savedChunks);

      if (canUpdateCompletedState && !matchesLastSavedResponse && !readOnly && complete) {
        markStepAsIncompletedByIndex(responseIndex);
      } else if (canUpdateCompletedState && matchesLastSavedResponse && !readOnly && !complete) {
        markStepAsCompletedByIndex(responseIndex);
      }
    }, [
      canUpdateCompletedState,
      readOnly,
      complete,
      responseIndex,
      markStepAsCompletedByIndex,
      markStepAsIncompletedByIndex,
      processSteps,
      response?.context,
      convertProcessStepsToChunks,
    ]);

    const maybeSubmitResponse = useCallback(
      (skipResetHistory: boolean = false) => {
        const shouldSubmit = getShouldSubmit(complete, ratings, processSteps);
        if (!shouldSubmit) {
          return;
        }

        if (error) {
          return;
        }

        // TODO have this be related to whatever is marked as a "good" step
        const correctChunks = processSteps.filter((chunk, index) => index <= firstBadRatingIndex);

        const allPreviousCorrectChunks = previousResponsesForCurrentPrompt
          .map(response => response?.context?.correctChunks || [])
          .filter(Boolean) as IProcessSupervisionChunk[][];

        const correctChunksAggregated: IProcessSupervisionChunk[] = allPreviousCorrectChunks
          .reduce((acc, val) => acc.concat(val), [])
          .concat(correctChunks);

        // Check if all of the steps are labeled, or if the question is marked as solved
        let isComplete = getIsComplete(
          processSteps,
          ratings,
          isQuestionSolvedByRewrite,
          endAfterRatingIds,
        );

        // Flamingo Reasoning R8 special logic:
        // If all chunks are correct but the response has is_eot=false, this means we only received a partial response
        // and the question remains unsolved. Thus we must set isComplete to false to continue the response generation
        const allChunksAreCorrect =
          processSteps.length === Object.keys(ratings).length &&
          _.every(Object.values(ratings), (rating: any) => rating >= 0);

        if (
          step.params.flamingo_enable_partial_responses &&
          allChunksAreCorrect &&
          mostRecentMessageMetadata?.is_eot === false
        ) {
          isComplete = false;
        }

        // Flamingo Reasoning R8 special logic:
        // We need to set special flags on message metadata if the response is incomplete or complete
        const messageMetadata = {
          ...mostRecentMessageMetadata,
        };

        if (step.params.flamingo_set_flags_on_incomplete_responses) {
          if (!isComplete) {
            messageMetadata.is_eot = false;
            messageMetadata.is_complete = false;
          }
          if (isComplete) {
            messageMetadata.is_eot = true;
            messageMetadata.is_complete = true;
          }
        }

        // Construct the preview/output of the response
        // @ts-ignore
        const output = getStepOutput(
          processSteps,
          {
            strategy: processChunkingStrategyName as ProcessChunkingStrategy,
            params: processChunkingStrategyParams,
          },
          firstBadRatingIndex,
          { params: step.params },
          rewriteFieldId,
          rewriteRatingIds,
        );

        // it should continue if:
        // it is the last process supervision, but the process is not yet complete and we want to re-request models
        // OR
        // it is not the last process supervision, and we want to continue
        const isLastProcessSuperision =
          numProcessSupervisions >= (config.maxProcessSupervisions || 1);

        const triggerEndOfConvo =
          ((!isComplete && reRequestModel) || !isLastProcessSuperision) === false;

        // Certain models expect the user message to be skipped to continue generating the response
        const skipPromptInput = step.params.can_skip_prompt_input_if_incomplete && !isComplete;

        const responseToSubmit: any = {
          type: InteractionStepType.ProcessSupervision,
          output,
          context: {
            isComplete, // If this flag is false, then we would auto-submit the MTC with a dummy user message
            triggerEndOfConvo,
            message: chatResponse?.output,
            chunks: convertProcessStepsToChunks(processSteps),
            isQuestionSolvedByRewrite,
            correctChunks,
            correctChunksAggregated,
            // TODO refactor the rating keys to be custom
            ratingCount: {
              '-1': correctChunks.filter(chunk => chunk.rating === -1).length,
              '0': correctChunks.filter(chunk => chunk.rating === 0).length,
              '1': correctChunks.filter(chunk => chunk.rating === 1).length,
            },
            ratingCountTotal: {
              '-1': correctChunksAggregated.filter(chunk => chunk.rating === -1).length,
              '0': correctChunksAggregated.filter(chunk => chunk.rating === 0).length,
              '1': correctChunksAggregated.filter(chunk => chunk.rating === 1).length,
            },
            prompt: mostRecentPrompt,
            critiqueData: processCritiqueHook.critiqueData,
            multiturnContinueOutput: !skipPromptInput
              ? mostRecentPrompt
              : 'Model will regenerate downstream chunks',
            skipPromptInput, // If this flag is True, we auto-skip the MTC and skip the user message
            messageMetadata,
          },
        };

        if (
          minimumNegativeRatings &&
          correctChunksAggregated.filter(chunk => endAfterRatingIds.includes(chunk.rating)).length <
            minimumNegativeRatings
        ) {
          setError(
            `You must have ${minimumNegativeRatings} negative ratings to submit. Please try a new prompt.`,
          );

          return;
        }

        const shouldResetHistory = skipResetHistory
          ? false
          : getShouldResetHistory(
              response,
              responseToSubmit,
              !triggerEndOfConvo,
              reRequestModel,
              endAfterRatingIds,
            );

        onSubmit(responseToSubmit, responseIndex, step, shouldResetHistory);
      },
      [
        complete,
        ratings,
        processSteps,
        chatResponse?.output,
        error,
        previousResponsesForCurrentPrompt,
        firstBadRatingIndex,
        isQuestionSolvedByRewrite,
        reRequestModel,
        mostRecentPrompt,
        minimumNegativeRatings,
        response,
        onSubmit,
        responseIndex,
        step,
        processCritiqueHook.critiqueData,
        numProcessSupervisions,
        config.maxProcessSupervisions,
        endAfterRatingIds,
        processChunkingStrategyName,
        processChunkingStrategyParams,
        rewriteRatingIds,
        convertProcessStepsToChunks,
        mostRecentMessageMetadata,
      ],
    );

    // Skips the entire turn
    const skipTurn = useCallback(() => {
      if (!step.params.allow_skip) {
        return;
      }
      onSubmit(
        {
          type: InteractionStepType.ProcessSupervision,
          output: processSteps.map(chunk => chunk.content).join('\n'),
          context: {
            shouldContinue: true,
            contextType: ContextType.Skipped,
          },
        },
        responseIndex,
        step,
        undefined,
      );
    }, [onSubmit, responseIndex, step, processSteps]);

    return (
      <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
        <StepCard readOnly={readOnly}>
          <StepCard.Header>
            <StepCard.HeaderLeft>
              <StepCard.Icon icon="award" />
              <StepCard.HeaderColumn>
                <StepCard.Title>{step.title || DEFAULT_PROCESS_SUPERVISION_TITLE}</StepCard.Title>
                <StepCard.Subtitle>
                  {step.subtitle || DEFAULT_PROCESS_SUPERVISION_SUBTITLE}
                </StepCard.Subtitle>
              </StepCard.HeaderColumn>
            </StepCard.HeaderLeft>
            <StepCard.HeaderRight>
              {!taskContext.enableUnifiedFeedback && (
                <CommentButton
                  visible={true}
                  childId={constructStepChildId(step)}
                  turnIndex={step.turn}
                />
              )}
              <PinButton responseIndex={responseIndex} />
              <ChatMessageDisplayConfigButton
                chatMessageDisplayConfig={chatMessageDisplayConfig}
                useMarkdownDisplay={useMarkdownDisplay}
                updateMarkdownConfig={updateMarkdownConfig}
                toggleChatMessageDisplayType={toggleChatMessageDisplayType}
              />
            </StepCard.HeaderRight>
          </StepCard.Header>
          <StepCard.Body className="flex flex-col">
            <div className="bg-neutral-100 p-2 rounded">
              <Typography.Paragraph2 className="text-xs">
                Prompt: {mostRecentPrompt}
              </Typography.Paragraph2>
            </div>
            {shouldUseProcessCritique && (
              <div>
                <ProcessCritique
                  critiqueKey="ground_truth"
                  processCritiqueHook={processCritiqueHook}
                  mostRecentPrompt={mostRecentPrompt}
                  processSteps={processSteps}
                  enabledReviewLevels={processCritiqueReviewLevels}
                  hasGtfa={hasGtfa}
                />
              </div>
            )}
            {processStepsIsLoading ? (
              <div>
                {Array.from({ length: 10 }, (_, index) => (
                  <Skeleton key={index} className="w-full mb-2 h-8 rounded" />
                ))}
              </div>
            ) : processStepsError ? (
              <Alert variant="warning" className="my-2">
                <Typography.Paragraph2>
                  Error loading steps: {processStepsError as unknown as React.ReactNode}
                </Typography.Paragraph2>
              </Alert>
            ) : (
              <>
                {!processSteps?.length && !isEditingProcessSteps && (
                  <>
                    <Alert variant="warning" className="p-2 my-2">
                      <Typography.Paragraph2>
                        {step.params.start_blank
                          ? 'No initial response provided. Please use "Edit chunks" to manually construct the chunked response.'
                          : 'We were unable to automatically create chunks for the model response. Please use "Edit chunks" to manually split the model response into chunks.'}
                      </Typography.Paragraph2>
                    </Alert>
                    <div className="bg-neutral-100 p-4 rounded-md">
                      <ChatMarkdown
                        markdown={chatResponse?.output || ''}
                        enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                        enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
                        enableSingleDollarTextMath={
                          chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                        }
                        enableSoftBreaks={true}
                      />
                    </div>
                    <br />
                  </>
                )}
                {isEditingProcessSteps && (
                  <ProcessStepsEditor
                    title={
                      step.params.edit_chunks_title || step.params.start_blank
                        ? DEFAULT_PROCESS_SUPERVISION_CREATE_CHUNKS_TITLE
                        : DEFAULT_PROCESS_SUPERVISION_EDIT_CHUNKS_TITLE
                    }
                    processSteps={processSteps}
                    setProcessSteps={setProcessSteps}
                    originalContent={chatResponse?.output}
                    onFinish={() => setIsEditingProcessSteps(false)}
                    disableManualChunkValidation={step.params.disable_manual_chunk_validation}
                  />
                )}
                {!isEditingProcessSteps &&
                  processSteps.map((chunk: ProcessSupervisionChunk, index: number) => {
                    const isLastItem = index === processSteps.length - 1;
                    return (
                      <div
                        key={index}
                        className={`flex flex-col gap-1 w-full items-center justify-between ${
                          isLastItem ? '' : 'border-b border-neutral-200'
                        } py-2 ${index > firstBadRatingIndex ? 'opacity-50' : ''}`}
                      >
                        <div className="flex flex-row gap-4 items-center w-full justify-between">
                          <Typography.Subtitle3 className="flex flex-col items-start gap-2">
                            <ChatMarkdown
                              className={`text-sm max-w-[520px] ${
                                rewrites?.[index] &&
                                getFieldsToCollectAtIndex(index).some(
                                  field => field.fieldId === rewriteFieldId,
                                )
                                  ? 'opacity-50'
                                  : ''
                              }`}
                              markdown={chunk.content}
                              enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                              enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
                              enableSingleDollarTextMath={
                                chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                              }
                              enableSoftBreaks={true}
                            />
                            {rewrites?.[index] && !useMilkdown && (
                              <ChatMarkdown
                                markdown={rewrites?.[index] || ''}
                                enableMath={enableLatex}
                              />
                            )}
                          </Typography.Subtitle3>
                          <div className="max-w-[300px] min-w-[200px] flex flex-wrap items-end flex-row-reverse gap-1">
                            {ratingValues.map(value => {
                              const buttonLabel = ratingValueToLabel(value)?.toString();
                              const isSelected = ratings?.[index]?.toString() === value.toString();

                              const variant = ratingButtons.find(
                                (rating: any) => rating.value === value,
                              )?.color;

                              // disable a rating with rewrite if minReflectionsBeforeRewrite is set and we haven't reached that yet
                              const shouldDisableRatingWithRewrite =
                                minReflectionsBeforeRewrite > 0 &&
                                numPreviousResponsesWithReflection < minReflectionsBeforeRewrite;

                              const doesRatingContainRewrite = rewriteRatingIds.includes(value);

                              const toolTipText =
                                shouldDisableRatingWithRewrite && doesRatingContainRewrite
                                  ? `Please provide ${minReflectionsBeforeRewrite} reflection(s) before rewriting.`
                                  : '';

                              return (
                                <Tooltip
                                  key={value.toString()}
                                  target={
                                    <div>
                                      <Button
                                        key={value.toString()}
                                        onClick={() => {
                                          if (readOnly || index > firstBadRatingIndex) {
                                            return;
                                          }
                                          setRatings(index, value);
                                        }}
                                        variant={isSelected ? (variant as any) : 'transparent'}
                                        // TODO ignore if we are not chaining processes together
                                        disabled={
                                          index > firstBadRatingIndex ||
                                          (shouldDisableRatingWithRewrite &&
                                            doesRatingContainRewrite)
                                        }
                                        className={twMerge(
                                          `inline-flex my-1 py-5 px-3 text-sm text-end rounded-xl font-medium transition break-words whitespace-normal`,
                                        )}
                                      >
                                        <Typography.Subtitle3>{buttonLabel}</Typography.Subtitle3>
                                      </Button>
                                    </div>
                                  }
                                  text={toolTipText}
                                />
                              );
                            })}
                          </div>
                        </div>
                        {!!getFieldsToCollectAtIndex(index)?.length && (
                          <div className="flex flex-col gap-2 items-start w-full border border-neutral-200 rounded-lg p-4">
                            {getFieldsToCollectAtIndex(index).map(field => {
                              return (
                                <ProcessSupervisionField
                                  key={field.fieldId}
                                  field={field}
                                  index={index}
                                  chunk={chunk}
                                  fieldEntries={fieldEntries}
                                  rewriteFieldId={rewriteFieldId}
                                  firstBadRatingIndex={firstBadRatingIndex}
                                  readOnly={!!readOnly}
                                  chatMessageDisplayConfig={chatMessageDisplayConfig}
                                  setFieldOnChunk={setFieldOnChunk}
                                />
                              );
                            })}
                            {/* check if there is a negative rating at this index */}
                            {reRequestModel &&
                              rewriteRatingIds?.includes(Number(ratings?.[index])) && (
                                <>
                                  <Typography.Subtitle3 className="flex items-center gap-2">
                                    {step.params.end_after_ratings_title ||
                                      DEFAULT_PROCESS_SUPERVISION_END_AFTER_RATINGS_TITLE}
                                  </Typography.Subtitle3>
                                  <SelectMenu
                                    options={[
                                      // @ts-ignore
                                      { id: true, name: 'Yes' },
                                      // @ts-ignore
                                      { id: false, name: 'No' },
                                      // @ts-ignore
                                      { id: undefined, name: '' },
                                    ]}
                                    selectedOptionId={isQuestionSolvedByRewrite as any}
                                    setSelected={(value: any) =>
                                      setIsQuestionSolvedByRewrite(value)
                                    }
                                    className="w-[80px]"
                                    disabled={!!readOnly}
                                  />{' '}
                                </>
                              )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                {error && !isEditingProcessSteps && (
                  <Alert variant="warning" className="my-2">
                    <Typography.Paragraph2>{error}</Typography.Paragraph2>
                  </Alert>
                )}
              </>
            )}
          </StepCard.Body>
          {!isEditingProcessSteps && (
            <StepCard.Footer>
              <div className="flex flex-row-reverse gap-2 w-full">
                {!taskContext.previewMode && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      maybeSubmitResponse(false);
                    }}
                    disabled={
                      !!error || (!processSteps?.length && !isEditingProcessSteps) || !!readOnly
                    }
                  >
                    <Icon
                      variant="white"
                      icon="check"
                      // @ts-ignore
                      size="sm"
                      className={`mr-2 ${error ? 'opacity-50' : ''}`}
                    />
                    Save and Continue
                  </Button>
                )}
                {!taskContext.previewMode && step.params?.enable_edit_without_reroll && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (
                        window.confirm(
                          'Are you sure you want to save changes without regenerating the rest of the conversation?',
                        )
                      ) {
                        maybeSubmitResponse(true);
                      }
                    }}
                    disabled={!!error || (!processSteps?.length && !isEditingProcessSteps)}
                  >
                    <Icon
                      variant="secondary"
                      icon="wand-magic-sparkles"
                      // @ts-ignore
                      size="sm"
                      className={`mr-2 ${error ? 'opacity-50' : ''}`}
                    />
                    Save without Regenerating
                  </Button>
                )}
                {step.params?.allow_skip && (
                  <Button
                    variant="neutral"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to skip this step?')) {
                        skipTurn();
                      }
                    }}
                  >
                    {/* @ts-ignore */}
                    <Icon icon="forward" size="sm" className="mr-2" />
                    Skip
                  </Button>
                )}
                {!step.params.disable_edit_chunks && (
                  <Button
                    variant="neutral"
                    onClick={() => {
                      setIsEditingProcessSteps(true);
                    }}
                    disabled={!!readOnly}
                  >
                    {/* @ts-ignore */}
                    <Icon icon="edit" size="sm" className="mr-2" />
                    Edit chunks
                  </Button>
                )}
              </div>
            </StepCard.Footer>
          )}
        </StepCard>
      </StepWidthWrapper>
    );
  };

interface ProcessSupervisionFieldProps {
  field: any;
  index: number;
  chunk: ProcessSupervisionChunk;
  fieldEntries: Record<keyof ProcessSupervisionChunk, any[]>;
  rewriteFieldId: string;
  firstBadRatingIndex: number;
  readOnly: boolean;
  setFieldOnChunk: (field: string, index: number, value: any) => void;
  chatMessageDisplayConfig: any;
}

const ProcessSupervisionField = ({
  field,
  index,
  chunk,
  fieldEntries,
  rewriteFieldId,
  firstBadRatingIndex,
  readOnly,
  setFieldOnChunk,
  chatMessageDisplayConfig,
}: ProcessSupervisionFieldProps) => {
  const taskContext = useContext(ChatTaskContext);
  const markdownSyntaxConfig = useMarkdownSyntaxOptions();

  const isRewriteField = field.fieldId === rewriteFieldId;
  const fieldValue = fieldEntries?.[field.fieldId]?.[index];
  const initialText = isRewriteField ? fieldValue || chunk.content : fieldValue || '';
  // editor toggle
  const [editorMode, setEditorMode] = useState<Mode>('editor');
  const selectedFieldType = field.type === 'plaintext' ? 'plain-text' : field.type;

  const markdownEditorComponent = (
    <MarkdownEditorContext
      enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
      defaultTokens={markdownSyntaxConfig}
      initialContent={initialText}
    >
      <EditorContainer>
        <MarkdownToolbar enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin} />
        <MarkdownFloatingMenu
          enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
        />
        <Editor
          content={initialText}
          onContentChange={newValue => {
            setFieldOnChunk(field.fieldId, index, newValue);
          }}
          disablePaste={taskContext.disableResponsePasting}
          readOnly={index > firstBadRatingIndex || readOnly}
        />
      </EditorContainer>
    </MarkdownEditorContext>
  );

  const plaintextEditorComponent = (
    <>
      <PlainTextEditorContext initialContent={initialText}>
        <EditorContainer className="w-full">
          <Editor
            content={initialText}
            onContentChange={value => setFieldOnChunk(field.fieldId, index, value)}
            readOnly={index > firstBadRatingIndex || readOnly}
          />
        </EditorContainer>
      </PlainTextEditorContext>
    </>
  );

  const selectComponent = ({ multiple }: { multiple: boolean }) => (
    <SelectMenu
      multiple={multiple}
      options={field.options.map((option: any) => ({
        id: option.value,
        name: option.label,
      }))}
      selectedOptionId={fieldEntries?.[field.fieldId]?.[index] || (multiple ? [] : undefined)}
      setSelected={(newValue: any) => {
        setFieldOnChunk(field.fieldId, index, newValue);
      }}
      disabled={index > firstBadRatingIndex || readOnly}
      className="w-full"
    />
  );

  const latexEditorComponent = (
    <LaTeXEditor
      initialText={initialText}
      onChange={(newValue: any) => {
        setFieldOnChunk(field.fieldId, index, newValue);
      }}
      readOnly={index > firstBadRatingIndex || readOnly}
      placeholder={field.description}
      enableSingleDollarTextMath={
        chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
      }
    />
  );

  return (
    <div
      key={field.fieldId}
      className={`flex flex-col gap-2 items-start w-full ${readOnly ? 'cursor-not-allowed' : ''}`}
    >
      <Typography.Subtitle3 className="flex items-center gap-2">
        {field.description} {field.required ? '(required)' : ''}
      </Typography.Subtitle3>
      <div className="flex flex-row justify-between w-full">
        {field.type === 'markdown' && (
          <ContentTypeSelector
            selectedMode={editorMode}
            onModeChange={setEditorMode}
            modeOptions={['editor']}
          />
        )}
        {field.type === 'plaintext' && (
          <ContentTypeSelector
            selectedMode={editorMode}
            onModeChange={setEditorMode}
            modeOptions={['editor', 'preview']}
          />
        )}
      </div>

      {editorMode === 'preview' && (
        <div className="w-full border border-neutral-200 overflow-hidden rounded-md min-h-[180px]">
          <div className="px-4 py-3">
            <ChatMarkdown
              className="text-sm"
              markdown={initialText}
              enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
              enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
              enableSingleDollarTextMath={
                taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
              }
              enableSoftBreaks={true}
            />
          </div>
        </div>
      )}

      {editorMode === 'editor' &&
        (selectedFieldType === 'markdown'
          ? markdownEditorComponent
          : field.type === 'select' && field.options
          ? selectComponent({ multiple: false })
          : field.type === 'multiselect' && field.options
          ? selectComponent({ multiple: true })
          : field.type === 'latex'
          ? latexEditorComponent
          : plaintextEditorComponent)}
    </div>
  );
};

export default memo(ProcessSupervisionTaskComponent);
